import React, { memo } from 'react';
import { InterPoint } from '@saviynt/common';
import { Chip, type ChipColorTypes, Icon } from '@saviynt/design-system';

const SEVERITY_MAP: Record<string, { text: string; color: ChipColorTypes }> = {
  CRITICAL: { text: 'Critical', color: 'critical' },
  VERY_HIGH: { text: 'Very High', color: 'pink' },
  HIGH: { text: 'High', color: 'orange' },
  MEDIUM: { text: 'Medium', color: 'warning' },
  LOW: { text: 'Low', color: 'success' },
};

const getMapKey = (value: string | null) => {
  if (!value) {
    return null;
  }

  const loweredValue = value.toLowerCase();

  if (loweredValue.includes('critical')) {
    return 'CRITICAL';
  }

  if (loweredValue.includes('very') && loweredValue.includes('high')) {
    return 'VERY_HIGH';
  }

  if (loweredValue.includes('high')) {
    return 'HIGH';
  }

  if (loweredValue.includes('medium')) {
    return 'MEDIUM';
  }

  if (loweredValue.includes('low')) {
    return 'LOW';
  }

  return null;
};

const capitalizeFirstLetter = (str: string) => {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

interface SoxCriticalProps {
  value: string;
  isIcon?: boolean;
  showForList?: boolean;
  isInterpoint?: boolean;
}

/**
 * SoxCritical
 */
function SoxCritical({
  value,
  isIcon,
  showForList,
  isInterpoint,
}: SoxCriticalProps) {
  const key = getMapKey(value);

  if (
    key === null ||
    (showForList && key !== 'VERY_HIGH' && key !== 'CRITICAL')
  ) {
    return showForList ? null : capitalizeFirstLetter(value);
  }

  const { text, color } = SEVERITY_MAP[key];

  return (
    <>
      {isInterpoint && <InterPoint />}
      <Chip
        size='xSmall'
        kind={showForList ? 'subtleFilled' : 'outlined'}
        label={text}
        color={color}
        leftIcon={isIcon ? <Icon kind='SOX' /> : undefined}
        isInteractionDisabled
      />
    </>
  );
}

SoxCritical.defaultProps = {
  isIcon: true,
  showForList: false,
  isInterpoint: false,
};

export default memo(SoxCritical);
